<template>
  <layout
    title="Trusted private authentication for banking"
    :transparent="true"
    class="overflow-x-hidden"
  >
    <highlightSection
      backgroundImage="/storage/images/industries/banking/banking_fraud_detection.jpg"
      altImage="Banking fraud detection"
      title="AI fraud detection and KYC compliance for Banking and Fintech"
      subtitle="Accelerate onboarding, minimize fraud, and automate KYC/AML checks."
      leftButton="See how it works"
      urlLeftButton="https://6992654.fs1.hubspotusercontent-na1.net/hubfs/6992654/01.%20ONE%20PAGERS/Flyer_Final_Version.pdf"
    ></highlightSection>

    <EndToEnd
      :endToEndData="endToEnd"
      title="Stop fraud before it starts with end-to-end ID verification"
    ></EndToEnd>

    <FeatureHighlightSection
      :features="features"
      title="Secure and seamless, from onboarding to payments"
    />
    <div class="pt-20">
      <cta
        title="Get started"
        text="Are you still sacrificing security over compliance?"
        buttonText="Book a Demo"
        buttonLink="/book-a-meeting"
        target="_blank"
      />
    </div>

    <div
      class="mt-20 flex md:flex-row flex-col items-center w-full mx-auto px-6 sm:px-7 md:px-4 lg:px-10 xl:px-16 max-w-screen-sm md:max-w-screen-xl gap-16"
    >
      <div
        class="flex justify-center flex-col md:max-w-full max-w-md md:text-left text-center"
      >
        <h3 class="text-3xl font-bold">100% proprietary, fully customizable</h3>
        <p class="mt-6">
          Empower your operations with proprietary technology tailored to your
          exact specifications.
        </p>
      </div>
      <rowCardsSection :elements="elements" :hoverAnimation="false" />
    </div>

    <partners />

    <latestPosts
      title="Read our latest articles and resources"
      :tags="['banking']"
    />

    <div class="mt-20">
      <cta
        title="Talk to our experts"
        text="Your competitors are already ahead. Are you ready?"
        buttonText="Contact us"
        buttonLink="/company#contactus"
      />
    </div>
  </layout>
</template>

<script>
const layout = () =>
  import(/* webpackChunkName: "layout" */ "../../Components/Layout/layout.vue");

const highlightSection = () =>
  import(
    /* webpackChunkName: "highlightSection" */ "../../Components/Layout/highlightSection.vue"
  );

const EndToEnd = () =>
  import(
    /* webpackChunkName: "ProductsInfoCards" */ "../../Components/Layout/UseCases/EndToEndSection.vue"
  );

const FeatureHighlightSection = () =>
  import(
    /* webpackChunkName: "FeatureHighlightSection" */ "../../Components/Layout/UseCases/FeatureHighlightSection.vue"
  );

const cta = () =>
  import(/* webpackChunkName: "cta" */ "../../Components/Layout/cta.vue");

const rowCardsSection = () =>
  import(
    /* webpackChunkName: "rowCardsSection" */ "../../Components/Layout/rowCardsSection.vue"
  );

const partners = () =>
  import(
    /* webpackChunkName: "partners" */ "../../Components/Layout/HomePage/partners.vue"
  );

const latestPosts = () =>
  import(
    /* webpackChunkName: "latestPosts" */ "../../Components/Layout/latestPosts.vue"
  );

export default {
  props: {
    errors: Object,
  },
  components: {
    layout,
    highlightSection,
    EndToEnd,
    FeatureHighlightSection,
    cta,
    rowCardsSection,
    partners,
    latestPosts,
  },
  data() {
    return {
      endToEnd: [
        {
          title: "Onboarding",
          iconPath: "/storage/images/icons/onboarding.svg",
          description: [
            "ID verification",
            "Document check",
            "Liveness and face match",
          ],
        },
        {
          title: "Compliance",
          iconPath: "/storage/images/icons/compliance.svg",
          description: ["KYC", "AML", "PEP/Sanctions/Adverse Media"],
        },
        {
          title: "Login",
          iconPath: "/storage/images/icons/login.svg",
          description: [
            "Passwordless login",
            "Multi-factor authentication",
            "Password recovery",
          ],
        },
        {
          title: "Transactions",
          iconPath: "/storage/images/icons/arrows-up-down.svg",
          description: [
            "Continuous authentication",
            "Fraud transaction prevention",
          ],
        },
      ],
      elements: [
        {
          title: `Identity Verification`,
          text: `With YouID, you can integrate identity verification flows into your business processes in just minutes.`,
          seeMoreUrl: `/products/youID`,
          imageSrc: `/storage/images/home/icons/identity_verification.svg`,
          imageAlt: `Identity Verification`,
          imageStyle: `w-14 h-14`,
        },
        {
          title: `User Authentication`,
          text: `With YouAuth, you can implement privacy-first user authentication into your app or website in a matter of minutes.`,
          seeMoreUrl: `/products/youAuth`,
          imageSrc: `/storage/images/home/user_authentication.svg`,
          imageAlt: `User Authentication`,
          imageStyle: `w-14 h-14`,
        },
      ],
      features: [
        {
          title: "Accelerate onboarding",
          description:
            "Enable seamless, secure, and automated identity verification that eliminates manual bottlenecks",
          image:
            "/storage/images/industries/banking/accelerate_onboarding_banking.png",
        },
        {
          title: "Minimize fraud",
          description:
            "Detect spoofing attempts, account takeovers, and identity theft in real time with top-tier face authentication.",
          image:
            "/storage/images/industries/banking/prevent_id_fraud_banking.png",
        },
        {
          title: "Ensure regulatory readiness",
          description:
            "Improve KYC compliance by integrating robust and customizable workflows that meet the most stringent global standards.",
          image:
            "/storage/images/industries/banking/ensure_kyc_compliance_banking.png",
        },
      ],
    };
  },
};
</script>
